import { Injectable } from '@angular/core';
import { openDB, IDBPDatabase } from 'idb';

// Define the schema
interface InvoiceDBSchema {
    invoices: {
        key: string;
        value: {
            ban: string;
            invoices: any[];
            lastUpdated: Date;
            isLoadMoreClicked: boolean;
            sessionId: string;
        };
    };
}

interface HistoricalInvoiceDBSchema {
    historicalInvoices: {
        key: string;
        value: {
            ban: string;
            historicalInvoices: any[];
            lastUpdated: Date;
            isLoadMoreClicked: boolean;
            sessionId: string;
        };
    };
}

@Injectable({
    providedIn: 'root',
})

export class IndexedDbService {
    private historicalInvoiceDB: Promise<IDBPDatabase<HistoricalInvoiceDBSchema>>;
    private invoiceDB: Promise<IDBPDatabase<InvoiceDBSchema>>;
    private expiryTime: number = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

    constructor() {
        this.invoiceDB = this.initInvoiceDB();
        this.historicalInvoiceDB = this.initHistoricalInvoiceDB();
    }

    private async initInvoiceDB(): Promise<IDBPDatabase<InvoiceDBSchema>> {
        return openDB<InvoiceDBSchema>('InvoiceDB', 1, {
            upgrade(invoiceDB: any) {
                if (!invoiceDB.objectStoreNames.contains('invoices')) {
                    invoiceDB.createObjectStore('invoices', { keyPath: 'ban' });
                }
            },
        });
    }

    private async initHistoricalInvoiceDB(): Promise<IDBPDatabase<HistoricalInvoiceDBSchema>> {
        return openDB<HistoricalInvoiceDBSchema>('HistoricalInvoiceDB', 1, {
            upgrade(historicalInvoiceDB: any) {
                if (!historicalInvoiceDB.objectStoreNames.contains('historicalInvoices')) {
                    historicalInvoiceDB.createObjectStore('historicalInvoices', { keyPath: 'ban' });
                }
            },
        });
    }

    // Store historicalInvoices with last updated time and sessionId
    async storeHistoricalInvoices(ban: string, historicalInvoices: any[], loadMoreStatus = false) {
        const sessionId = localStorage.getItem('SID') || '';
        const historicalInvoiceDB = await this.historicalInvoiceDB;
        await historicalInvoiceDB.put('historicalInvoices', {
            ban,
            historicalInvoices,
            lastUpdated: new Date(),
            isLoadMoreClicked: loadMoreStatus,
            sessionId,
        });
    }

    // Get historicalInvoices with data expiry check and sessionId check
    async getHistoricalInvoices(ban: string): Promise<any[] | null> {
        const sessionId = localStorage.getItem('SID') || '';
        const historicalInvoiceDB = await this.historicalInvoiceDB;
        const record = await historicalInvoiceDB.get('historicalInvoices', ban);
        if (record) {
            if (this.isDataExpired(record.lastUpdated) || record.sessionId !== sessionId) {
                await this.deleteHistoricalInvoices(ban);
                return null;
            }
            return record.historicalInvoices;
        }
        return null;
    }

    // Get load more status for historicalInvoices with sessionId check
    async getLoadHistoricalInvoicesCheck(ban: string): Promise<boolean | null> {
        const sessionId = localStorage.getItem('SID') || '';
        const historicalInvoiceDB = await this.historicalInvoiceDB;
        const record = await historicalInvoiceDB.get('historicalInvoices', ban);
        if (record) {
            if (this.isDataExpired(record.lastUpdated) || record.sessionId !== sessionId) {
                await this.deleteHistoricalInvoices(ban);
                return null;
            }
            return record.isLoadMoreClicked;
        }
        return null;
    }

    // Delete historicalInvoices for the given BAN
    async deleteHistoricalInvoices(ban: string) {
        const historicalInvoiceDB = await this.historicalInvoiceDB;
        await historicalInvoiceDB.delete('historicalInvoices', ban);
    }

    // Store invoices with last updated time and sessionId
    async storeInvoices(ban: string, invoices: any[], loadMoreStatus = false) {
        const sessionId = localStorage.getItem('SID') || '';
        const invoiceDB = await this.invoiceDB;
        await invoiceDB.put('invoices', {
            ban,
            invoices,
            lastUpdated: new Date(),
            isLoadMoreClicked: loadMoreStatus,
            sessionId,
        });
    }

    // Get invoices with data expiry check and sessionId check
    async getInvoices(ban: string): Promise<any[] | null> {
        const sessionId = localStorage.getItem('SID') || '';
        const invoiceDB = await this.invoiceDB;
        const record = await invoiceDB.get('invoices', ban);
        if (record) {
            if (this.isDataExpired(record.lastUpdated) || record.sessionId !== sessionId) {
                await this.deleteInvoices(ban);
                return null;
            }
            return record.invoices;
        }
        return null;
    }

    // Get load more status for invoices with sessionId check
    async getLoadInvoiceCheck(ban: string): Promise<boolean | null> {
        const sessionId = localStorage.getItem('SID') || '';
        const invoiceDB = await this.invoiceDB;
        const record = await invoiceDB.get('invoices', ban);
        if (record) {
            if (this.isDataExpired(record.lastUpdated) || record.sessionId !== sessionId) {
                await this.deleteInvoices(ban);
                return null;
            }
            return record.isLoadMoreClicked;
        }
        return false;
    }

    // Delete invoices for the given BAN
    async deleteInvoices(ban: string) {
        const invoiceDB = await this.invoiceDB;
        await invoiceDB.delete('invoices', ban);
    }

    // Check if data is expired based on lastUpdated
    private isDataExpired(lastUpdated: Date): boolean {
        const currentTime = new Date().getTime();
        const lastUpdatedTime = new Date(lastUpdated).getTime();
        return currentTime - lastUpdatedTime > this.expiryTime;
    }
}
